import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import { toApiError } from "src/types/api";
import axiosInstance from "src/store/features/auth/axiosCSRF";

interface FileInfo {
  file_name:string;
  file_extension:string;
  file_size:number | string;
  s3_patch?:string;
}

const normalizeFileExt = (ext?: string | null) =>
  ext?.replace(/^\./, "").trim().toLowerCase() || null;

const getGeneExpressionFileExtension = (response: any) =>
  normalizeFileExt(
    response?.gene_expression_file_extension ??
      response?.gene_expression_file_ext ??
      response?.gene_expression_file_info?.file_extension
  );

const getGeneExpressionFileName = (response: any) =>
  response?.gene_expression_file_name ??
  response?.gene_expression_file_info?.file_name;

const normalizeClinicalModel = (model: string) => model.trim().toLowerCase();

const normalizeClinicalModels = (models: string[]) =>
  Array.from(new Set(models.map(normalizeClinicalModel).filter(Boolean)));

const getDrugResponseTool = (response: any) =>
  Array.isArray(response?.drug_response_tool)
    ? response.drug_response_tool
    : Array.isArray(response?.clinical_models)
    ? response.clinical_models
    : Array.isArray(response?.model_names)
    ? response.model_names
    : [];

const parseCellLineString = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return [];

  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    const quotedItems = [...trimmed.matchAll(/'([^']*)'|"([^"]*)"/g)].map(
      (match) => match[1] ?? match[2] ?? ""
    );
    if (quotedItems.length > 0) return quotedItems;

    return trimmed
      .slice(1, -1)
      .split(",")
      .map((item) => item.replace(/^['"]|['"]$/g, ""));
  }

  return [trimmed];
};

const normalizeCellLines = (cellLines: unknown): string[] => {
  if (!Array.isArray(cellLines)) return [];

  const normalized = cellLines.flatMap((item) => {
    if (Array.isArray(item)) return normalizeCellLines(item);
    if (typeof item === "string") return parseCellLineString(item);
    return [];
  });

  return Array.from(
    new Set(normalized.map((item) => item.trim()).filter(Boolean))
  );
};

// 임시 저장 Filter Callback 에러 타입
interface ApiError {
  status?: number;
  code?: string;
  message: string;
  details?: any;
}

// workFlowFilterCallbackAPI 파라미터 타입 정의
interface WorkFlowFilterCallbackParams {
  sample_use?: boolean;
  library?: string | null;
  smiles?: string | null;
  patientFileInfo? : FileInfo | null;
  [key: string]: any;
}

// WorkFlowFileCallBackAPI 파라미터 타입 정의
export interface WorkflowGetCallBackFileList {
  group_name: string;
  library_name: string;
  round: number;
  workflow_name?: string;
  file_type?: string;
  sample_use?: boolean;
  savePatientCSV?: boolean;
}

// 통합된 iUserParameterState 인터페이스
export interface iDrugResponseUserParameterState {
  library?: string | null;
  smiles?: string | null;
  userFileChecking: string | null;
  userInputWorkSpaceName: string;
  userChoiceDrugResponseTool?: string;
  response: any;
  isFileCallbackLoading: boolean;
  isFilterCallbackLoading: boolean;
  message: string | null;
  status: number | null;
  error: string | null;
  userRunningStep: number;
  autoStep: string | null;
  cardExpanded: boolean;
  isLoadedFromTemporarySave: boolean;
  userHaveAssayFile: boolean;
  isSampleData: boolean;
  sample_use: boolean;
  apiError: ApiError | null;
  docking_smiles: string | null;
  cell_lines: undefined | string[];
  clinical_models: string[];
  gene_expression_file_name : string | null;
  gene_expression_file_extension  : string | null;
  isPatientCSVLoading: boolean;
  patientCSVResponse : any;
  containColumnCount : number;
  userChoiceUploadColumn : undefined | string[];
}

// 초기 상태
const initialState: iDrugResponseUserParameterState = {
  library: null,
  userFileChecking: null,
  smiles: null,
  userInputWorkSpaceName: "",
  userChoiceDrugResponseTool: null,
  response: null,
  isFileCallbackLoading: false,
  isFilterCallbackLoading: false,
  message: "",
  status: null,
  userHaveAssayFile: false,
  error: null,
  userRunningStep: null,
  autoStep: null,
  cardExpanded: false,
  isSampleData: false,
  sample_use: false,
  isLoadedFromTemporarySave: false,
  apiError: null,
  docking_smiles: null,
  cell_lines: [],
  clinical_models: ["pertda", "codeae", "therapi"],
  gene_expression_file_name : null,
  gene_expression_file_extension : null,
  isPatientCSVLoading: false,
  patientCSVResponse :null,
  containColumnCount:null,
  userChoiceUploadColumn :[]
};

// 임시저장 Filter Callback
export const temporarySaveFilterCallbackAPI = createAsyncThunk(
  "userDrugResponseParameter/temporarySaveFilterCallback",
  async (params: WorkFlowFilterCallbackParams = {}, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const pathParam = "/workflow/temp-callback/";

      if (!djangoEndpoint) {
        throw new Error("Django endpoint not found");
      }

      const requestBody = Object.keys(params).length > 0 ? params : {};

      const response = await axiosInstance.post(
        `${djangoEndpoint}${pathParam}`,
        requestBody,
        { withCredentials: true }
      );

      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.response?.data || apiError.message);
    }
  }
);

// 임시저장 File Callback
export const temporarySaveFileCallBackAPI = createAsyncThunk(
  "userDrugResponseParameter/temporarySaveFileCallBack",
  async (data: WorkflowGetCallBackFileList, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const pathParam = "/workflow/temp-file-callback/";

      const requestData: any = {
        group_name: data.group_name,
        library_name: data.library_name,
        workflow_name: data.workflow_name,
        round: data.round,
        file_type: data.file_type,
      };

      // sample_use 명확하게 전달
      if (data.sample_use !== undefined) {
        requestData.sample_use = data.sample_use;
      }
      const response = await axiosInstance.post(
        `${djangoEndpoint}${pathParam}`,
        requestData
      );

      return {
        sample_use: Boolean(data.sample_use), // 요청한 값 그대로 반환
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      console.error("❌ temporarySaveFileCallBackAPI Error:", error);
      return rejectWithValue(error);
    }
  }
);

// workFlowFilterCallbackAPI
export const workFlowFilterCallbackAPI = createAsyncThunk(
  "userParameter/workFlowFilterCallback",
  async (params: WorkFlowFilterCallbackParams = {}, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const pathParam = "/workflow/filter-callback/";

      if (!djangoEndpoint) {
        throw new Error("Django endpoint not found");
      }

      const requestBody = Object.keys(params).length > 0 ? params : {};

      const response = await axiosInstance.post(
        `${djangoEndpoint}${pathParam}`,
        requestBody,
        { withCredentials: true }
      );

      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.response?.data || apiError.message);
    }
  }
);

export const WorkFlowFileCallBackAPI = createAsyncThunk(
  "userParameter/WorkFlowFileCallBack",
  async (data: WorkflowGetCallBackFileList, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const pathParam = "/workflow/file-callback/";

      const requestData: any = {
        group_name: data.group_name,
        library_name: data.library_name,
        workflow_name: data.workflow_name,
        round: data.round,
        file_type: data.file_type,
      };

      if (data.sample_use !== undefined) {
        requestData.sample_use = data.sample_use;
      }

      // savePatientCSV도 백엔드에 전달
      if (data.savePatientCSV !== undefined) {
        requestData.savePatientCSV = data.savePatientCSV;
      }

      const response = await axiosInstance.post(
        `${djangoEndpoint}${pathParam}`,
        requestData
      );

      return {
        sample_use: Boolean(data.sample_use),
        savePatientCSVResponse: Boolean(data.savePatientCSV || false), // ✅ 이제 정확히 반영됨
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      console.error("❌ WorkFlowFileCallBackAPI Error:", error);
      return rejectWithValue(error);
    }
  }
);

// UserParameter Slice
export const userDrugResponseParameterSlice = createSlice({
  name: "userDrugResponseParameter",
  initialState,
  reducers: {
    setAutoStepSave: (state, action) => {
      state.autoStep = action.payload;
    },
    setUserFileUploadChecking: (state, action) => {
      state.userFileChecking = action.payload;
    },
    setUserInputSmile: (state, action) => {
      state.smiles = action.payload;
    },
    setUserRunningStep: (state, action) => {
      state.userRunningStep = action.payload;
    },
    setIsLoadedFromTemporarySave: (state, action: PayloadAction<boolean>) => {
      state.isLoadedFromTemporarySave = action.payload;
    },

    resetAllParametersExceptLibrary: (state, action) => {
      const libraryName = action.payload;

      Object.assign(state, {
        ...initialState,
        library: libraryName,
      });
    },
    resetAllParameters: (state) => {
      const currentLibrary = state.library;
      Object.assign(state, {
        ...initialState,
        library: currentLibrary,
      });
    },
    clearSmiles: (state) => {
      state.smiles = null;
    },
    setUserDrugResponseChoiceTool: (state, action) => {
      state.userChoiceDrugResponseTool = action.payload;
    },
    // reducer에 추가
    setUserInputWorkSpaceName: (state, action) => {
      state.userInputWorkSpaceName = action.payload;
      // Workspace 변경 시 sample_use 초기화
      if (action.payload !== state.userInputWorkSpaceName) {
        state.isSampleData = false;
      }
    },
    resetWorkflowResult: (state) => {
      state.response = null;
      state.status = null;
      state.message = "";
      state.isFileCallbackLoading = false;
      state.isFilterCallbackLoading = false;
    },
    clearWorkflowError: (state) => {
      state.message = "";
      state.status = null;
      state.error = null;
    },
    resetForSampleData: (state) => {
      state.isSampleData = false;
    },
    setUserHaveAssayFile: (state, action) => {
      state.userHaveAssayFile = action.payload;
    },
    setApiError: (state, action: PayloadAction<ApiError | null>) => {
      state.apiError = action.payload;
    },
    setContainColumnList: (state, action: PayloadAction<number | null>) => {
      state.containColumnCount = action.payload
    },
    clearApiError: (state) => {
      state.apiError = null;
    },
    setDockingSmiles: (state, action: PayloadAction<string | null>) => {
      state.docking_smiles = action.payload;
    },
    resetAllFileInfo: (state) => {
      state.userFileChecking = null;
      state.userHaveAssayFile = false;
    },
    setSelectedCellLines : (state, action: PayloadAction<string[]>) =>{
      state.cell_lines = normalizeCellLines(action.payload);
    },
    setSelectedClinicalModels : (state, action: PayloadAction<string[]>) =>{
      state.clinical_models = normalizeClinicalModels(action.payload);
    },
    setSelectedUploadColumnList : (state, action: PayloadAction<string[]>) =>{
      state.userChoiceUploadColumn = action.payload
    },
    setSelectedGeneFileName : (state, action: PayloadAction<string | null>) =>{
      const normalizedName = action.payload?.trim() ?? null;
      state.gene_expression_file_name = normalizedName || null;
    },
    setSelectedGeneFileExt : (state, action: PayloadAction<string | null>) =>{
      const normalizedExt = action.payload
        ?.replace(/^\./, "")
        .trim()
        .toLowerCase();
      state.gene_expression_file_extension = normalizedExt || null;
    },
    setPatientResponse:  (state, action: PayloadAction<any>) => {
      state.patientCSVResponse = action.payload;
    },
    resetPatientResponse: (state) =>{
      state.gene_expression_file_name = null;
      state.gene_expression_file_extension = null;
      state.patientCSVResponse = null;
      state.containColumnCount = null;
      state.userChoiceUploadColumn = [];
    }
  },

  extraReducers: (builder) => {
    builder
      // ==================== temporarySaveFilterCallbackAPI ====================
      .addCase(temporarySaveFilterCallbackAPI.pending, (state) => {
        state.isLoadedFromTemporarySave = false;
        state.error = null;
      })
      .addCase(temporarySaveFilterCallbackAPI.fulfilled, (state, action) => {
        state.isFilterCallbackLoading = false;
        state.isLoadedFromTemporarySave = true;
        state.error = null;
        const response = action.payload;

        // sample_use 처리
        if (response?.sample_use !== undefined) {
          state.sample_use = response.sample_use;
          state.isSampleData = response.sample_use;
        }
        if (response?.smiles !== undefined) {
          state.smiles = response.smiles;
        }
        if (response?.cell_lines !== undefined) {
          state.cell_lines = normalizeCellLines(response.cell_lines);
        }
        const drugResponseTool = getDrugResponseTool(response);
        if (drugResponseTool.length > 0) {
          state.clinical_models = normalizeClinicalModels(drugResponseTool);
        }
        const geneExpressionFileName = getGeneExpressionFileName(response);
        if (geneExpressionFileName !== undefined) {
          state.gene_expression_file_name = geneExpressionFileName;
        }
        const geneExpressionFileExt = getGeneExpressionFileExtension(response);
        if (geneExpressionFileExt !== null) {
          state.gene_expression_file_extension = geneExpressionFileExt;
        }
        if (response?.kinase_info) {
          const kinaseEntries = Object.entries(response.kinase_info);

          kinaseEntries.forEach(
            ([kinaseName, kinaseData]: [string, any]) => {}
          );
        }
      })
      .addCase(temporarySaveFilterCallbackAPI.rejected, (state, action) => {
        state.isFilterCallbackLoading = false;
        state.error = action.payload as string;

        // 에러 상세 정보 저장
        const error = action.payload as any;
        state.apiError = {
          status: error?.status || error?.response?.status,
          code: error?.code || error?.response?.data?.code,
          message:
            error?.message ||
            error?.response?.data?.message ||
            "An error occurred",
          details: error?.response?.data,
        };
      })

      // ==================== temporarySaveFileCallBackAPI ====================
      .addCase(temporarySaveFileCallBackAPI.pending, (state) => {
        state.isFileCallbackLoading = true;
        state.status = null;
        state.message = "";
      })
      // temporarySaveFileCallBackAPI.fulfilled도 수정
      .addCase(temporarySaveFileCallBackAPI.fulfilled, (state, action) => {
        state.isFileCallbackLoading = false;
        state.status = action.payload.status;

        if (action.payload.sample_use !== undefined) {
          state.sample_use = action.payload.sample_use;
          state.isSampleData = action.payload.sample_use;
        }

        // 조건 없이 그냥 저장하거나, 필요없으면 제거
        state.patientCSVResponse = action.payload.data;
      })
      .addCase(temporarySaveFileCallBackAPI.rejected, (state, action) => {
        state.isFileCallbackLoading = false;

        const errorPayload = action.payload as any;
        const requestData = action.meta.arg;

        console.error("temporarySaveFileCallBackAPI Error:", {
          requestData,
          errorPayload,
        });

        if (typeof errorPayload === "object" && errorPayload !== null) {
          state.message = errorPayload.error?.message || "An error occurred";
          state.status = errorPayload.error?.response?.status || null;
        } else {
          state.message = (errorPayload as string) || "An error occurred";
        }
      })

      .addCase(workFlowFilterCallbackAPI.pending, (state) => {
        state.isFilterCallbackLoading = true;
        state.error = null;
      })
      .addCase(workFlowFilterCallbackAPI.fulfilled, (state, action) => {
        state.isFilterCallbackLoading = false;
        state.isLoadedFromTemporarySave = false;
        state.error = null;
        const response = action.payload;
        const isSampleUse = action.meta.arg?.sample_use === true;
        if (isSampleUse) {
          state.sample_use = true;
          state.isSampleData = true;
        }

        if (response?.sample_use !== undefined) {
          state.sample_use = response.sample_use;
          state.isSampleData = response.sample_use;
        }

        if (isSampleUse) {
          state.isSampleData = true;
        }
        if (response?.sample_use !== undefined) {
          state.sample_use = response.sample_use;
        }

        if (response?.smiles !== undefined) {
          state.smiles = response.smiles;
        }

        if (response?.cell_lines !== undefined) {
          state.cell_lines = normalizeCellLines(response.cell_lines);
        }
        const drugResponseTool = getDrugResponseTool(response);
        if (drugResponseTool.length > 0) {
          state.clinical_models = normalizeClinicalModels(drugResponseTool);
        }
        const geneExpressionFileName = getGeneExpressionFileName(response);
        if (geneExpressionFileName !== undefined) {
          state.gene_expression_file_name = geneExpressionFileName;
        }
        const geneExpressionFileExt = getGeneExpressionFileExtension(response);
        if (geneExpressionFileExt !== null) {
          state.gene_expression_file_extension = geneExpressionFileExt;
        }
      })
      .addCase(workFlowFilterCallbackAPI.rejected, (state, action) => {
        state.isFilterCallbackLoading = false;
        state.error = action.payload as string;
      })

      .addCase(WorkFlowFileCallBackAPI.pending, (state, action) => {
        state.isFileCallbackLoading = true;
        state.status = null;
        state.message = "";
      })
      .addCase(WorkFlowFileCallBackAPI.fulfilled, (state, action) => {
        state.isFileCallbackLoading = false;
        state.status = action.payload.status;
      
        const responseData = action.payload.data;
      
        if (action.payload.sample_use !== undefined) {
          state.sample_use = action.payload.sample_use;
          state.isSampleData = action.payload.sample_use;
        }
      
        // 조건 제거하고 무조건 저장 (임시 확인용)
        state.patientCSVResponse = responseData;
        state.isPatientCSVLoading = false;
      
        state.response = responseData;
      })
      .addCase(WorkFlowFileCallBackAPI.rejected, (state, action) => {
        state.isFileCallbackLoading = false;

        const errorPayload = action.payload as any;
        const requestData = action.meta.arg;

        console.error("WorkFlowFileCallBackAPI Error:", {
          requestData,
          errorPayload,
        });

        if (requestData.savePatientCSV) {
          state.isPatientCSVLoading = false;
        }

        if (typeof errorPayload === "object" && errorPayload !== null) {
          state.message = errorPayload.error?.message || "An error occurred";
          state.status = errorPayload.error?.response?.status || null;
        } else {
          state.message = (errorPayload as string) || "An error occurred";
        }
      });
  },
});

export const {
  setUserFileUploadChecking,
  setUserInputSmile,
  resetAllParametersExceptLibrary,
  resetAllParameters,
  setUserDrugResponseChoiceTool,
  setUserInputWorkSpaceName,
  resetWorkflowResult,
  clearWorkflowError,
  resetForSampleData,
  setUserRunningStep,
  setSelectedGeneFileName,
  setSelectedGeneFileExt,
  setAutoStepSave,
  setDockingSmiles,
  setUserHaveAssayFile,
  setIsLoadedFromTemporarySave,
  setApiError,
  clearApiError,
  setPatientResponse,
  clearSmiles,
  resetAllFileInfo,
  setSelectedCellLines,
  setSelectedClinicalModels,
  setContainColumnList,
  setSelectedUploadColumnList,
  resetPatientResponse
} = userDrugResponseParameterSlice.actions;

export default userDrugResponseParameterSlice.reducer;
